import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Download, Loader2, Users, MapPin, Phone, Mail, AlertTriangle, Check, UserCheck, Code2, ChevronRight } from 'lucide-react';
import { LemitSocio, LemitTipo, LemitPessoaData, LemitEmpresaData } from '../../../types';
import { StagedRow, DonoInfo, maskDoc, onlyDigits, docTipoFromDigits, definirRepresentanteRow, proximoPassoEnriquecimento } from '../../../src/services/prospeccaoLista';
import { getLatestLemitConsulta } from '../../../src/services/lemit';
import EnriquecimentoPanel, { enrToLemitLike, socioPessoaFromEnr } from '../components/Lemit/EnriquecimentoPanel';
import RepresentanteModal from '../components/Lemit/RepresentanteModal';

type ViewRow = StagedRow & { dono?: DonoInfo | null };

interface Props {
    row: ViewRow | null;
    onClose: () => void;
    onEnrich: (row: ViewRow) => void;
    onImport: (row: ViewRow) => void;
    onPatch?: (row: StagedRow) => void;
    enriching: boolean;
    importing: boolean;
}

const fmtFone = (d?: string | null) => {
    const n = onlyDigits(d);
    if (n.length < 10) return d || '—';
    const ddd = n.slice(0, 2), resto = n.slice(2);
    return `(${ddd}) ${resto.slice(0, resto.length - 4)}-${resto.slice(-4)}`;
};

const ProspeccaoRowModal: React.FC<Props> = ({ row, onClose, onEnrich, onImport, onPatch, enriching, importing }) => {
    const [showRepModal, setShowRepModal] = useState(false);
    const [salvandoRep, setSalvandoRep] = useState(false);
    // Dossiê COMPLETO da última consulta salva (lemit_consultas) — mesmo dado da
    // Central Lemit (situação, fundação, CNAE, quadro completo + JSON cru). Custo
    // zero (só lê o cache); o enriquecimento_data encenado é só um subconjunto.
    const [full, setFull] = useState<{ data: LemitPessoaData | LemitEmpresaData; raw: any } | null>(null);

    useEffect(() => {
        setFull(null);
        const d = onlyDigits(row?.documento || row?.cnpj);
        if (!row || (d.length !== 11 && d.length !== 14) || row.enriquecimento_status === 'nao') return;
        const tipoDoc: LemitTipo = d.length === 14 ? 'empresa' : 'pessoa';
        let cancel = false;
        getLatestLemitConsulta(d, tipoDoc)
            .then((c) => { if (!cancel && c?.resultado) setFull({ data: c.resultado, raw: c.resultado_raw ?? c.resultado }); })
            .catch(() => { /* sem consulta salva: mantém o subconjunto encenado */ });
        return () => { cancel = true; };
    }, [row?.place_id, row?.enriquecimento_status]);

    if (!row) return null;
    const doc = onlyDigits(row.documento || row.cnpj);
    const tipo = (row.doc_tipo || docTipoFromDigits(doc)) === 'cnpj' ? 'empresa' : 'pessoa';
    const enr = row.enriquecimento_data;
    const importavel = row.status !== 'importado' && !row.dono;
    const jaEnriquecido = row.enriquecimento_status !== 'nao' && row.enriquecimento_status !== 'falhou';
    const passo = proximoPassoEnriquecimento(row);

    // Prioriza o dossiê completo (Central Lemit); cai no subconjunto encenado
    // enquanto carrega ou se não houver consulta salva.
    const lemitLike = full?.data ?? enrToLemitLike(enr as any, tipo, doc, row.razao_social || row.nome);

    return createPortal(
        <>
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-fade-in" onClick={onClose}>
            <div className="bg-gray-50 dark:bg-slate-950 rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="relative px-5 sm:px-6 py-4 bg-gradient-to-r from-[#5F6C37] to-[#3f4a24] text-white flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2.5 bg-white/15 rounded-xl backdrop-blur-sm"><Sparkles size={20} /></div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-extrabold leading-tight truncate">{row.nome}</h2>
                            <p className="text-[11px] text-white/80 font-medium">{maskDoc(doc)} · {tipo === 'empresa' ? 'Pessoa Jurídica' : 'Pessoa Física'}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/15 rounded-full transition-colors"><X size={20} /></button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
                    {/* Resumo de contato + status */}
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        {row.dono ? (
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                <UserCheck size={13} /> Já é lead de {row.dono.responsavel_nome || '—'}
                            </span>
                        ) : row.status === 'importado' ? (
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                <Check size={13} /> Importado
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                Disponível
                            </span>
                        )}
                        {(enr?.melhor_whatsapp || row.telefone) && (
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-semibold">
                                <Phone size={12} /> {fmtFone(enr?.melhor_whatsapp || row.telefone)}
                            </span>
                        )}
                        {(enr?.melhor_email || row.email) && (
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 font-semibold truncate max-w-[240px]">
                                <Mail size={12} /> {enr?.melhor_email || row.email}
                            </span>
                        )}
                        {row.endereco && (
                            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 font-semibold truncate max-w-[280px]">
                                <MapPin size={12} /> {row.endereco}
                            </span>
                        )}
                    </div>

                    {row.import_erro && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/40 text-xs font-semibold">
                            <AlertTriangle size={14} /> Falha no último import: {row.import_erro}
                        </div>
                    )}

                    {jaEnriquecido ? (
                        <EnriquecimentoPanel
                            data={lemitLike}
                            tipo={tipo}
                            socioData={socioPessoaFromEnr(enr as any)}
                            representantePendente={!!enr?.representante_pendente}
                            onEscolherRepresentante={(enr?.socios?.length || 0) > 1 ? () => setShowRepModal(true) : undefined}
                            // Na Lista Fria a consulta do dono faz parte do próprio fluxo de enriquecimento
                            onConsultarSocio={passo === 'buscar_dono' ? () => onEnrich(row) : undefined}
                            consultandoSocio={enriching}
                            highlight={{ socioPrincipalCpf: enr?.socio_principal?.cpf, representante: enr?.representante }}
                        />
                    ) : (
                        <div className="text-center py-10 text-slate-400">
                            <Sparkles size={32} className="mx-auto mb-3 opacity-30" />
                            <p className="text-sm">Ainda não enriquecido pela Lemit. Enriqueça para puxar telefones, e-mails{tipo === 'empresa' ? ', sócios e o contato do dono' : ' e endereço'}.</p>
                        </div>
                    )}

                    {/* Retorno completo (JSON) — igual à Central Lemit */}
                    {full?.raw && (
                        <details className="group bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl overflow-hidden">
                            <summary className="flex items-center gap-2.5 px-4 py-3 cursor-pointer select-none list-none">
                                <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500"><Code2 size={14} /></div>
                                <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Retorno completo (JSON)</span>
                                <ChevronRight size={16} className="ml-auto text-slate-400 group-open:rotate-90 transition-transform" />
                            </summary>
                            <pre className="px-4 pb-4 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300 overflow-x-auto max-h-72 overflow-y-auto">
                                {JSON.stringify(full.raw, null, 2)}
                            </pre>
                        </details>
                    )}
                </div>

                {/* Footer ações — um passo por vez, nada de repetir o processo */}
                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex-shrink-0">
                    {passo === 'completo' ? (
                        <span className="mr-auto flex items-center gap-1.5 text-xs font-bold text-violet-600 dark:text-violet-400">
                            <Check size={14} /> Enriquecimento concluído
                            {/* O representante é escolhido sozinho; quando foi cara ou coroa
                                (empate real), oferecemos a troca aqui mesmo. */}
                            {enr?.representante_criterio === 'ordem_lemit' && (enr?.socios?.length || 0) > 1 && (
                                <button onClick={() => setShowRepModal(true)}
                                    className="ml-2 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 font-bold">
                                    dono por desempate · trocar
                                </button>
                            )}
                        </span>
                    ) : (
                        <button onClick={() => onEnrich(row)} disabled={enriching}
                            className={`mr-auto flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-full disabled:opacity-40 ${passo === 'buscar_dono'
                                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100'
                                : 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 hover:bg-violet-100'}`}
                            title={passo === 'buscar_dono' ? 'Empresa já consultada — falta o CPF do dono' : undefined}>
                            {enriching ? <Loader2 size={13} className="animate-spin" /> : passo === 'buscar_dono' ? <Users size={13} /> : <Sparkles size={13} />}
                            {passo === 'buscar_dono' ? 'Buscar dados do dono' : passo === 'tentar_novamente' ? 'Tentar de novo' : 'Enriquecer'}
                        </button>
                    )}
                    {importavel && (
                        <button onClick={() => onImport(row)} disabled={importing}
                            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-full bg-primary text-white hover:opacity-90 disabled:opacity-40">
                            {importing ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Importar lead
                        </button>
                    )}
                </div>
            </div>
        </div>

        <RepresentanteModal
            isOpen={showRepModal}
            onClose={() => setShowRepModal(false)}
            socios={(enr?.socios || []) as LemitSocio[]}
            empresaNome={row.razao_social || row.nome}
            cpfAtual={enr?.representante?.cpf}
            salvando={salvandoRep}
            onConfirm={async (socio) => {
                setSalvandoRep(true);
                try {
                    const upd = await definirRepresentanteRow(row, socio);
                    onPatch?.(upd);
                    setShowRepModal(false);
                } finally {
                    setSalvandoRep(false);
                }
            }}
        />
        </>,
        document.body,
    );
};

export default ProspeccaoRowModal;
